// src/phase4/ui/HealthDisplay.ts
// Health display component - Pure Phaser UI component
// Displays base health as hearts

import Phaser from 'phaser';

export class HealthDisplay extends Phaser.GameObjects.Container {
  private healthText?: Phaser.GameObjects.Text;
  private healthBar?: Phaser.GameObjects.Graphics;
  private bg?: Phaser.GameObjects.Graphics;
  private currentHealth: number = 0;
  private barWidth: number = 100;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
    this.createDisplay();
    this.setDepth(100);
  }

  private createDisplay(): void {
    // Background
    this.bg = this.scene.add.graphics();
    this.bg.fillStyle(0x201e1a, 0.9);
    this.bg.fillRoundedRect(-70, -18, 140, 36, 6);
    this.bg.lineStyle(1, 0x3a362e);
    this.bg.strokeRoundedRect(-70, -18, 140, 36, 6);
    this.add(this.bg);

    // Heart icon
    const heartIcon = this.scene.add.text(-55, 0, '❤️', {
      fontSize: '18px',
    }).setOrigin(0.5);
    this.add(heartIcon);

    // Health bar background
    const barBg = this.scene.add.graphics();
    barBg.fillStyle(0x2a2824, 1);
    barBg.fillRoundedRect(-25, -12, this.barWidth, 24, 4);
    this.add(barBg);

    // Health bar fill
    this.healthBar = this.scene.add.graphics();
    this.healthBar.setDepth(this.depth + 1);
    this.add(this.healthBar);

    // Health text
    this.healthText = this.scene.add.text(0, 0, '×3', {
      fontSize: '16px',
      color: '#e05545',
      fontFamily: 'monospace',
      fontStyle: 'bold',
    }).setOrigin(0, 0.5);
    this.add(this.healthText);

    this.updateHealthBar(3, 3);
  }

  private updateHealthBar(current: number, max: number): void {
    if (!this.healthBar) return;

    this.healthBar.clear();

    if (max <= 0) return;

    const fillPercent = Math.max(0, Math.min(1, current / max));
    const fillWidth = this.barWidth * fillPercent;

    // Color based on health percentage
    let barColor = 0x5cd6a8; // Green
    if (fillPercent < 0.3) {
      barColor = 0xe05545; // Red
    } else if (fillPercent < 0.6) {
      barColor = 0xe6b832; // Orange/Yellow
    }

    this.healthBar.fillStyle(barColor, 1);
    this.healthBar.fillRoundedRect(-25, -12, fillWidth, 24, 4);
  }

  update(current: number, max: number): void {
    const wasDamaged = current < this.currentHealth;
    this.currentHealth = current;

    // Update hearts display
    this.healthText?.setText(`×${current}`);

    // Update health bar
    this.updateHealthBar(current, max);

    // Flash red when damaged
    if (wasDamaged) {
      this.scene.tweens.add({
        targets: this.bg,
        alpha: { from: 1, to: 0.3, duration: 50 },
        yoyo: true,
        repeat: 2,
      });
    }
  }

  getHealth(): number {
    return this.currentHealth;
  }

  setPosition(x: number, y: number): this {
    super.setPosition(x, y);
    return this;
  }

  destroy(): void {
    super.destroy();
  }
}

export default HealthDisplay;
